import { useEffect, useRef, type ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { Spinner } from './Spinner';

interface BlockingOverlayProps {
  open: boolean;
  children?: ReactNode;
}

// Covers the WHOLE app — sidebar, header, any open modal — while something must not be interrupted,
// e.g. a write that a second write would step on. Portaled to <body> so no parent's overflow or stacking
// context can leave a gap. z-[2000] sits above modals (1050/1060) and below toasts (3000), so a result
// toast still shows.
//
// Pointer input is swallowed by the backdrop; keyboard input is handled by pulling focus onto the overlay
// and refusing Tab, so a focused button behind it cannot be pressed with Enter or Space.
export function BlockingOverlay({ open, children }: BlockingOverlayProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open || typeof document === 'undefined') return null;
  return ReactDOM.createPortal(
    <div
      ref={ref}
      role="alertdialog"
      aria-modal="true"
      aria-busy="true"
      aria-live="assertive"
      tabIndex={-1}
      onKeyDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="blocking-overlay fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-[1px] cursor-wait outline-none"
    >
      <div className="flex items-center gap-3 rounded-xl bg-white px-5 py-4 shadow-lg dark:bg-zinc-900">
        <Spinner size="sm" />
        <div>{children}</div>
      </div>
    </div>,
    document.body
  );
}
